import * as dd from 'dingtalk-jsapi';

/**
 * 钉钉 PC 工作台容器免登。
 *
 * 调用流程（与 H5 端 apps/h5/src/dingtalk.ts 同源）：
 *   1. 用户在钉钉 PC 工作台点开「瑞小美人才盘点」应用，URL 在钉钉容器内打开
 *   2. dd.ready 后 requestAuthCode({ corpId }) 拿到一次性 code
 *   3. 前端 POST /api/auth/dingtalk/h5 { authCode } 给后端
 *   4. 后端用 code → userid → 工号 → 颁发 JWT，前端落 token
 *
 * 普通浏览器（非钉钉容器）下 dd.env.platform === 'notInDingTalk'，
 * isInDingTalk() 返回 false，调用方应回退到登录页（密码登录通道，
 * 也是管理员"代登录"通道）。
 */

const AUTH_CODE_TIMEOUT_MS = 10_000;

export const isInDingTalk = (): boolean => {
  // dingtalk-jsapi 的类型定义里 env 是 optional，且 platform 不在公开类型上；
  // 这里走 any 取值与 H5 端口径保持一致（apps/h5/src/dingtalk.ts）。
  const env = (dd as unknown as { env?: { platform?: string } }).env;
  return !!env?.platform && env.platform !== 'notInDingTalk';
};

function whenDingTalkReady(): Promise<void> {
  return new Promise((resolve) => {
    try {
      const ready = (dd as unknown as { ready?: (cb: () => void) => void }).ready;
      if (typeof ready === 'function') {
        ready(() => resolve());
        return;
      }
    } catch {
      /* ignore */
    }
    resolve();
  });
}

export const getDingtalkAuthCode = async (corpId: string): Promise<string> => {
  if (!isInDingTalk()) {
    throw new Error('当前不在钉钉环境');
  }
  if (!corpId) {
    throw new Error('钉钉 CorpId 未配置');
  }

  await whenDingTalkReady();

  const authPromise = dd.runtime.permission
    .requestAuthCode({ corpId })
    .then((res) => (res as { code: string }).code);

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<string>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error('钉钉授权超时，请刷新或改用姓名口令登录')),
      AUTH_CODE_TIMEOUT_MS,
    );
  });

  try {
    return await Promise.race([authPromise, timeoutPromise]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};
